import React, { useEffect, useContext, useState, useCallback, useReducer } from 'react';
import * as history from 'history';
import { Redirect, Route, Router, Switch, useHistory, useLocation } from "react-router-dom";
import { Provider, useDispatch, useSelector } from 'react-redux';
import configureStore from '@state/store';
import _ from "lodash";
import ComingSoon from '@routes/private/ComingSoon';
import ErrorPage from '@routes/private/ErrorPage';
import PreviewConstructor from '@routes/private/PreviewConstructor';
import Editor from '@routes/private/Editor';
import MyDesigns from '@routes/private/MyDesigns';
import SharedFiles from '@routes/private/SharedFiles';
import Templates from '@routes/private/Templates';
import BrandLibrary from '@routes/private/BrandLibrary';
import NewTemplate from '@routes/private/Templates/components/NewTemplate';
import StudioDashboard from '@routes/private/Studio';
import Snackbar from '@components/Snackbar';
import actions from '@state/actions';
import selectors from '@state/selectors';
import ErrorBoundary from '@components/ErrorBoundary';
import { AuthProvider } from '@utils/DOIgnition/AuthContext';
import { DesignsProvider } from '@utils/DOIgnition/DesignsContext';
import { StudioProvider } from '@utils/DOIgnition/StudioContext';
import { openNewTemplateAction } from '@state/openReducers/actions';
import { IAppState } from '@state/types';
import { getParams } from '@utils/userUtils';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { BrandsProvider } from '@utils/DOIgnition/BrandsContext';
import { IDSConfig } from '@state/dsconfig/types';
import { IUserType } from '@state/auth/types';
import { ISnackbar } from '@state/openReducers/types'
import { generateToken } from '@state/storage/utils';
import UserManagement from '@routes/private/UserManagement';
import customDomains from '@utils/customDomains';

declare let pendo: any;
// Setup store
const store = configureStore();

function NoMatch() {
  let location = useLocation();
  return (
    <div>
      <h3>
        No match for <code>{location.pathname}</code>
      </h3>
    </div>
  );
}

function SecuredApp() {
  const authContext = useContext(AuthContext);
  const isAdmin = authContext.userData && authContext.userData.profile && authContext.userData.profile.role === IUserType.ADMIN;
  const hasTemplateAccess = Boolean(authContext?.userData?.profile.role === IUserType.DEALER_USER && authContext.dsPermissions?.includes("TEMPLATE_MANAGEMENT"));
  const userData = authContext && authContext.userData?authContext.userData.profile:undefined;
  const isLeadScienceUser = authContext && authContext.LeadScienceUser ? authContext.LeadScienceUser : false;

  const openNewTemplateState = useSelector<IAppState, IAppState["openNewTemplateState"]>((state) => state.openNewTemplateState).openNewTemplate;
  const templatesResponse = useSelector(selectors.templates.getTemplateResponse);
  const designsResponse = useSelector(selectors.designs.getDesignResponse);
  const designFavoriteResponse = useSelector(selectors.designs.getDesignFavoriteResponse);
  const updatedFieldTagsResponse = useSelector(selectors.parsedTrees.getUpdatedFieldTagsResponse);
  const addUpdateBrandResponse = useSelector(selectors.brands.getAddUpdateBrandsnackBar);
  const designAuth = useSelector(selectors.designs.getDesignAuth);
  const designAuthMailErros = useSelector(selectors.designs.getDesignAuthMailErrors);
  const showsnackBar: ISnackbar|undefined  = useSelector(selectors.open.getShowsnackBar);
  const dispatch = useDispatch();
  const historyRouter = useHistory()

  const [message, setMessage] = useState({value: "", type: ""});
  const [hideCloseIcon, setHideCloseIcon] = useState(false);
  const [style, setStyle] = useState<any | undefined>(undefined);
  const [debouncedState, setDebouncedState] = useState("");
  
  const fetchDSConfig = () => {
    return new Promise((resolve, reject) => {
      const dealerUserData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User",
        role: authContext?.userData.profile.role ? authContext?.userData.profile.role : "",
        organizations: authContext?.userData.profile.name ? authContext.userData.profile["dealeron.dealerorganization"] : []
    };  
    dispatch(actions.dsConfig.fetchDSConfig(dealerUserData, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const runGetDSConfig = async () => {
    const result = await fetchDSConfig() as IDSConfig;
  }

  //Pendo Initialize
    React.useEffect(() => {
      if (userData ) {
          //for Pendo insight          
          pendo.initialize({
              visitor: {
                  id: userData.sub,
                  email: userData.email,                  
                  role:  userData.role
              },
              account: {
                  id: userData["dealeron.primarydealer"]
              }
          });
          //end for Pendo insight
      }        
  }, [userData])


  useEffect(() => {    
    runGetDSConfig();
    verifyRedirectToLSDomain(isLeadScienceUser,true);
    return updatePage(historyRouter.location);
  }, [])

  useEffect(() => {
    return historyRouter.listen((location) => {
      updatePage(location);
    })
  }, [historyRouter])

  useEffect(() => {
    if (templatesResponse) {
      if (templatesResponse.status === 200) {
        if (templatesResponse.designName !== undefined && templatesResponse.designName !== '') {
          setMessage({value: `"${templatesResponse.designName}" was successfully saved as a template! You can view it in Template Management.`, type: "success"});
        }
        else {
          setMessage({value: `"${templatesResponse.name}" was successfully uploaded!`, type: "success"});
        }
        debounce(templatesResponse.name);
      } else if (templatesResponse.status >= 400) {
        setMessage({value: `"${templatesResponse.name}" could not be uploaded.`, type: "error"});
      }
    }
  }, [templatesResponse])

  useEffect(() => {
    if (designAuthMailErros !== undefined && designAuth != undefined && designAuth.action && (designAuth.action === "ADD" || designAuth.action === "UPDATE")) {
      if (designAuthMailErros.length == 0) {
        setHideCloseIcon(true);
        setMessage({value: "Invite(s) sent successfully", type: "success"});
      }
      else {
        setMessage({value: "There was an error sending the invite(s): " + designAuthMailErros.join(' '), type: "error"});
      }
    }
  }, [designAuthMailErros]);

  useEffect(() => {
    if (designsResponse) {
      if (designsResponse.status === 200) {
        setMessage({value: `"${designsResponse.name}" was successfully created!`, type: "success"});
        debounce(designsResponse.name);
      } else if (designsResponse.status >= 400) {
        setMessage({value: `"${designsResponse.name}" could not be created.`, type: "error"});
      }
    }
  }, [designsResponse])

  useEffect(() => {
    if (designFavoriteResponse) {
      let state = `added to`;
      if (!designFavoriteResponse.favorite) state = `removed from`
      
      if (designFavoriteResponse.status === 200) {
        setMessage({value: `"${designFavoriteResponse.name}" was successfully ${state} your favorite designs!`, type: "success"});
        debounce(designFavoriteResponse.name);
      } else if (designFavoriteResponse.status >= 400) {
        setMessage({value: `"${designFavoriteResponse.name}" could not be ${state}.`, type: "error"});
      }
    }
  }, [designFavoriteResponse])

  useEffect(() => {
    if (updatedFieldTagsResponse?.snackBar) {
      let info =  updatedFieldTagsResponse.snackBar
      
      if (updatedFieldTagsResponse.status === 200) {
        setStyle({backgroundColor: "white", color: "#000;"});
        setMessage({value: `${info.count} ${info.count>1 ? 'elements' : 'element'} successfully added to ${info.categoryName}.`, type: "success"});
      } else if (designFavoriteResponse.status >= 400) {
        setMessage({value: `The elements could not be added to ${info.categoryName}.`, type: "error"});
      }
    }
  }, [updatedFieldTagsResponse])

  useEffect(() => {
    if (addUpdateBrandResponse?.snackBar) {
      if (addUpdateBrandResponse.status === 200) {
        setStyle({backgroundColor: "rgba(18, 36, 66, 0.8)", color: "#fff", width: "176px", height: "18px"});
        setMessage({value: addUpdateBrandResponse.snackBar, type: "success"});
      } else if (designFavoriteResponse.status >= 400) {
        setMessage({value: `The information could not be saved.`, type: "error"});
      }
    }
  }, [addUpdateBrandResponse])

  useEffect(() => {
    if (showsnackBar?.open) {
      setStyle({height: "24px", fontSize: "8px"});
      setMessage({value: showsnackBar.messagge, type: showsnackBar.typeMessage});
    }
  }, [showsnackBar])
  

  const debounce = useCallback(
    _.debounce((_searchVal: string) => {
      setDebouncedState(_searchVal);
      updateTemplates();		
    }, 1000),
    []
  );

  const updateTemplates = async () => {
    await fetchTemplates();
  }

  const fetchTemplates = () => {
    let query=`?filter=c.templateStatus<>'INACTIVE'&userdata=${JSON.stringify(userData)}&orderby=c.templateStatus DESC,c.createdDate DESC&start=0&pagelength=20`;
    return new Promise((resolve, reject) => {
      dispatch(actions.templates.fetchTemplates(query, (result: any, error: any) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const restrictAccess = (location: history.Location, push: boolean) => {
    //UnComment to restrict access to dealer users
    if (location.pathname.search("editor") === -1 && userData?.role === IUserType.NOTALLOWED_DEALER_USER) {
      localStorage.clear();
      dispatch(actions.auth.setAuth(undefined, undefined, undefined));
      if (push) historyRouter.push({pathname: "/coming-soon"});
      else window.location.href = "/coming-soon";
    }
  }

  const verifyRedirectToLSDomain = (isLeadScienceUser: boolean,RedirectToLSDomain: boolean) => {
    let domainsList = customDomains ? customDomains : ['https://designstudio.leadscience.com','https://designstudio.dealeron.com'];
    //const AcceptedDomain = isLeadScienceUser ? "https://designstudio.leadscienceqa.com" : "http://localhost:3000";
    const AcceptedDomain = isLeadScienceUser ? domainsList[0] : domainsList[1];
    const currentDomain = window.location.href??window.location.hostname;
    if (RedirectToLSDomain && !currentDomain.includes(AcceptedDomain)) {
      let currentUrl = AcceptedDomain + window.location.pathname;
      window.location.assign(currentUrl)
    }
  }

  const updatePage = (location:any) => {
    const params = getParams(location);
    if (params.page) {
      if ((isAdmin || hasTemplateAccess) && params.page === "new-template") {
        dispatch(openNewTemplateAction(true));         
      } else {
        historyRouter.push({});
      }
    } else {
      dispatch(openNewTemplateAction(false));
    }
    generateToken(dispatch);
  }

  const onFolderActions = (item: any, action: string, status: string, folder?:number) => {
    if (folder || folder===0) {
      if (action == "DELETE") {
        if (status == "success") {
          setMessage({value: `Folder "${item}" deleted.`, type: status});
        }
        else {
          setMessage({value: `There was an error deleting the folder "${item}".`, type: status});
        }
      }
      if (action == "MOVEDESIGNS") {
        if (status == "success") {
          setMessage({value: `Designs moved to folder "${item}".`, type: status});
        } 
        else {
          setMessage({value: `There was an error moving to folder "${item}".`, type: status});
        }       
      }
    } 
  }

  return (
    <>
      <Switch>
        <Route exact path="/"><Redirect to="/studio" /></Route>          
        <Route exact path="/studio"><StudioProvider><StudioDashboard /></StudioProvider></Route>
        <Route exact path="/my-designs"><DesignsProvider><MyDesigns onFolderActions={onFolderActions} /></DesignsProvider></Route>
        <Route exact path="/shared-files"><DesignsProvider><SharedFiles/></DesignsProvider></Route>
        <Route exact path="/templates"><Templates /></Route>
        <Route exact path="/brand-library"><BrandsProvider><BrandLibrary /></BrandsProvider></Route>
        <Route exact path="/user-management"><UserManagement></UserManagement></Route>
        <Route exact path="/editor/:type/:id"><Editor /></Route>
        <Route exact path="/demo"><PreviewConstructor /></Route>
        <Route exact path="/coming-soon"><ComingSoon /></Route>
        <Route path="*"><NoMatch /></Route> 
      </Switch>
      {
        openNewTemplateState &&
        <NewTemplate />
      }
      <Snackbar 
        message={message}
        style={style && style}
        hideCloseIcon = {hideCloseIcon}
        setHideCloseIcon = {setHideCloseIcon}
      />
    </>
  )
}

function TryApp() {
  const [errors, onError] = useReducer(
    (state: Error[], error: Error) => state.concat(error),
    []
  );

  try {
    if (errors.length) throw errors[0];
    return (
      <ErrorBoundary
        onError={onError}
      >
        <SecuredApp />
      </ErrorBoundary>
    );
  } catch {
    return <ErrorPage />
  }
}

function App() {
  return (
    <Provider store={store}>
      <Router history={history.createBrowserHistory()}>
        <AuthProvider>
          <TryApp />
        </AuthProvider>
      </Router>
    </Provider>
  );
}

export default App;
